/** The asset's own fields as a form draft: from an asset, to a create body, and to a patch of what changed. */

import { AssetCreateIn as AssetCreateSchema } from '@/api/generated/schemas';
import type { AssetCreateIn, AssetKind, AssetMaturity, AssetOut, AssetUpdateIn } from '@/api/types';
import { fieldErrors, oneOf, type FieldErrors } from '@/api/validate';

export interface ExpertDraft {
  user_id: string;
  display_name: string;
  email: string;
  label: string;
  /** Shown beside a provisioned expert who has not signed in yet. */
  note?: string | null;
}

export interface AssetFieldsDraft {
  name: string;
  kind: AssetKind | '';
  tagline: string;
  summary: string;
  maturity: AssetMaturity;
  home_country: string;
  industry_ids: string[];
  /** Partner names, free text: the server reuses a name it has and creates the rest. */
  partners: string[];
  countries: string[];
  experts: ExpertDraft[];
}

/** The fields a person types or picks by name; ids and codes come from the server's own lists. */
const TYPED = AssetCreateSchema.pick({ name: true, kind: true, tagline: true, summary: true, partners: true });

export const EMPTY_FIELDS: AssetFieldsDraft = {
  name: '',
  kind: '',
  tagline: '',
  summary: '',
  maturity: 'concept',
  home_country: 'GLOBAL',
  industry_ids: [],
  partners: [],
  countries: [],
  experts: [],
};

export function fieldsOf(asset: AssetOut): AssetFieldsDraft {
  return {
    name: asset.name,
    kind: oneOf(AssetCreateSchema.shape.kind, asset.kind, ''),
    tagline: asset.tagline,
    summary: asset.summary,
    maturity: oneOf(AssetCreateSchema.shape.maturity.unwrap().options[0], asset.maturity, 'concept'),
    home_country: asset.home_country,
    industry_ids: asset.industries.map((i) => i.id),
    partners: asset.partners.map((p) => p.name),
    countries: asset.countries.map((c) => c.code),
    experts: [...asset.experts]
      .sort((a, b) => a.position - b.position)
      .map((e) => ({ user_id: e.user_id, display_name: e.display_name, email: e.email, label: e.label ?? '' })),
  };
}

/** What the API would refuse in the typed fields, by field. */
export function draftErrors(draft: AssetFieldsDraft): FieldErrors {
  const { name, kind, tagline, summary, partners } = draft;
  return fieldErrors(TYPED, { name, kind, tagline, summary, partners });
}

export function fieldsValid(draft: AssetFieldsDraft): boolean {
  return Object.keys(draftErrors(draft)).length === 0;
}

function expertsIn(draft: AssetFieldsDraft) {
  return draft.experts.map((e) => ({ user_id: e.user_id, label: e.label.trim() || null }));
}

export function createBody(draft: AssetFieldsDraft): AssetCreateIn {
  return {
    name: draft.name.trim(),
    kind: draft.kind as AssetKind,
    tagline: draft.tagline.trim(),
    summary: draft.summary.trim(),
    maturity: draft.maturity,
    home_country: draft.home_country,
    industry_ids: draft.industry_ids,
    partners: draft.partners,
    countries: draft.countries,
    experts: expertsIn(draft),
  };
}

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
const expertKey = (d: AssetFieldsDraft) => JSON.stringify(expertsIn(d));

const SCALARS = ['name', 'kind', 'tagline', 'summary', 'maturity', 'home_country'] as const;
const LISTS = ['industry_ids', 'partners', 'countries'] as const;

/** Only what differs from `initial`; an empty object when nothing changed. */
export function changedFields(initial: AssetFieldsDraft, draft: AssetFieldsDraft): Omit<AssetUpdateIn, 'revision'> {
  const next = createBody(draft);
  const patch: Record<string, unknown> = {};
  for (const key of SCALARS) if (next[key] !== initial[key]) patch[key] = next[key];
  for (const key of LISTS) if (!sameList(draft[key], initial[key])) patch[key] = draft[key];
  if (expertKey(draft) !== expertKey(initial)) patch.experts = expertsIn(draft);
  return patch as Omit<AssetUpdateIn, 'revision'>;
}
